/**
 * Helper que mapeia o output do drawing_extractor (PDF 2D) para sugestões
 * de pré-preenchimento dos campos da RFQ e Quotation.
 *
 * Decisões UX:
 *  - confidence ≥ 0.85 → auto-apply (não pede consentimento)
 *  - confidence < 0.85 → sugestão pendente (utilizador escolhe via checkbox)
 *  - campo já preenchido → ignorado por defeito (não sobrescreve)
 *  - liga sem correspondência exacta → tenta fuzzy match; se falhar
 *    devolve a sugestão como "informational" com warning
 *  - Para a altura (piece_height_mm), aplicamos um aviso explícito quando
 *    a confidence do candidato top-3 é baixa (< 0.7).
 *
 * O painel "Info detectada no desenho 2D" continua a ser a fonte de
 * verdade da extracção; este helper só resume as sugestões accionáveis.
 */

const AUTO_APPLY_THRESHOLD = 0.85;
const HEIGHT_WARNING_THRESHOLD = 0.7;

/**
 * Procura uma liga na lista por código, fazendo fuzzy match.
 *
 * Estratégia (ordem):
 *  1. Match exacto (case-insensitive, sem espaços nem traços a mais).
 *  2. Match por prefixo: alloy.code é prefixo do extraído OU vice-versa.
 *  3. Match por substring compacta (substituindo "-" por nada para tolerar
 *     "AC44300DF" ↔ "AC-44300DF").
 *  4. Match por **core canónico** AC-NNNNN: extrai o núcleo numérico de
 *     ambos os lados, ignorando prefixos como "EN", "EN A" e sufixos de
 *     variante como "DF", "HF", "TF", "-A", "-B". Resolve casos comuns:
 *       "AC- 47100DF -A"  →  EN AC-47100
 *       "AC-44300DF"       →  EN AC-44300
 *       "EN AC-44300DF"    →  EN AC-44300
 *  5. Sem match → null.
 *
 * Devolve { match, kind } onde kind ∈ 'exact'|'prefix'|'fuzzy'|'core'|null.
 */
export function findAlloyMatch(extractedCode, alloys) {
  if (!extractedCode || !Array.isArray(alloys) || alloys.length === 0) {
    return { match: null, kind: null };
  }
  const norm = (s) => String(s || '').toUpperCase().replace(/\s+/g, '').trim();
  const compact = (s) => norm(s).replace(/-/g, '');
  // Núcleo canónico: "AC" + 4–6 dígitos. Ignora prefixo "EN"/"EN A" e
  // sufixos como "DF", "HF", "TF", "-A", "-B".
  const core = (s) => {
    const c = compact(s);
    const m = c.match(/AC(\d{4,6})/);
    return m ? `AC${m[1]}` : null;
  };

  const target = norm(extractedCode);
  const targetCompact = compact(extractedCode);
  const targetCore = core(extractedCode);

  for (const a of alloys) {
    if (norm(a.code) === target) return { match: a, kind: 'exact' };
  }
  for (const a of alloys) {
    const c = norm(a.code);
    if (c.startsWith(target) || target.startsWith(c)) {
      return { match: a, kind: 'prefix' };
    }
  }
  for (const a of alloys) {
    const c = compact(a.code);
    if (c === targetCompact) return { match: a, kind: 'fuzzy' };
    if (c.startsWith(targetCompact) || targetCompact.startsWith(c)) {
      return { match: a, kind: 'fuzzy' };
    }
  }
  if (targetCore) {
    for (const a of alloys) {
      if (core(a.code) === targetCore) return { match: a, kind: 'core' };
    }
  }
  return { match: null, kind: null };
}

/**
 * Heurística para decidir se um campo "já tem valor".
 *
 * Tratamos `null`, `undefined`, string vazia e `0` como "vazio" (porque
 * `0` é o default do backend para campos numéricos não preenchidos), exceto
 * para campos onde 0 pode ser intencional. Para os campos cobertos pelas
 * sugestões (peso, dimensões, designação) tratar 0 como vazio é o
 * comportamento correcto.
 */
function isEmptyValue(v) {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (typeof v === 'number') return v === 0 || Number.isNaN(v);
  return false;
}

/**
 * Constrói a lista de sugestões para uma RFQ.
 *
 * @param {object} params
 * @param {object} params.extraction - Output do GET /extraction (drawing 2D)
 * @param {Array}  params.alloys - Lista de ligas configuradas no sistema
 * @param {object} params.rfq - RFQ actual (para verificar already-filled)
 * @param {object} params.quotation - Quotation actual
 * @returns {Array<Suggestion>}
 *
 * Cada Suggestion tem:
 *   - id: string único (estável entre renders)
 *   - path: 'rfq.part_reference' | 'rfq.part_designation' |
 *           'quotation.alloy_code' | 'quotation.piece_weight_kg' |
 *           'quotation.piece_length_mm' | 'quotation.piece_width_mm' |
 *           'quotation.piece_height_mm'
 *   - section: 'rfq' | 'quotation'
 *   - field: nome da chave dentro da secção
 *   - labelKey: chave i18n para o label
 *   - value: valor a aplicar (string ou number)
 *   - displayValue: representação humana (com unidade)
 *   - confidence: número 0..1
 *   - source: chave i18n descrevendo a origem (part_number, weight_kg, ...)
 *   - autoApply: bool (true se confidence ≥ 0.85 e !alreadyHasValue)
 *   - alreadyHasValue: bool
 *   - warning?: chave i18n a mostrar como aviso (ex: liga não encontrada,
 *               altura baixa confiança)
 *   - infoOnly?: bool — sugestão informativa, não aplicável (ex: liga
 *               sem match na config)
 */
export function buildSuggestions({ extraction, alloys, rfq, quotation }) {
  const suggestions = [];
  if (!extraction) return suggestions;

  const sf = extraction.structured_fields || {};
  const cf = extraction.structured_fields_confidence || {};
  const dims = extraction.dimension_candidates || [];

  const push = (s) => {
    s.autoApply = !s.alreadyHasValue && !s.infoOnly && s.confidence >= AUTO_APPLY_THRESHOLD;
    suggestions.push(s);
  };

  if (sf.part_number) {
    push({
      id: 'part_reference',
      path: 'rfq.part_reference',
      section: 'rfq',
      field: 'part_reference',
      labelKey: 'suggest_part_reference',
      value: String(sf.part_number),
      displayValue: String(sf.part_number),
      confidence: cf.part_number ?? 0.7,
      source: 'field_part_number',
      alreadyHasValue: !isEmptyValue(rfq?.part_reference),
    });
  }

  if (sf.designation) {
    push({
      id: 'part_designation',
      path: 'rfq.part_designation',
      section: 'rfq',
      field: 'part_designation',
      labelKey: 'suggest_part_designation',
      value: String(sf.designation),
      displayValue: String(sf.designation),
      confidence: cf.designation ?? 0.7,
      source: 'field_designation',
      alreadyHasValue: !isEmptyValue(rfq?.part_designation),
    });
  }

  if (sf.weight_kg != null) {
    const conf = cf.weight_kg ?? 0.6;
    push({
      id: 'piece_weight_kg',
      path: 'quotation.piece_weight_kg',
      section: 'quotation',
      field: 'piece_weight_kg',
      labelKey: 'suggest_piece_weight',
      value: Number(sf.weight_kg),
      displayValue: `${Number(sf.weight_kg).toFixed(3)} kg`,
      confidence: conf,
      source: 'field_weight',
      alreadyHasValue: !isEmptyValue(quotation?.piece_weight_kg),
      warning: conf < AUTO_APPLY_THRESHOLD ? 'suggest_warn_weight_no_unit' : undefined,
    });
  }

  if (sf.material_alloy) {
    const { match, kind } = findAlloyMatch(sf.material_alloy, alloys);
    if (match) {
      push({
        id: 'alloy_code',
        path: 'quotation.alloy_code',
        section: 'quotation',
        field: 'alloy_code',
        labelKey: 'suggest_alloy',
        value: match.code,
        displayValue: kind === 'exact'
          ? match.code
          : `${match.code} (≈ ${sf.material_alloy})`,
        confidence: kind === 'exact' ? (cf.material_alloy ?? 0.9) : 0.75,
        source: 'field_material',
        alreadyHasValue: !isEmptyValue(quotation?.alloy_code),
      });
    } else {
      // Liga sem correspondência — sugestão informativa
      push({
        id: 'alloy_code',
        path: 'quotation.alloy_code',
        section: 'quotation',
        field: 'alloy_code',
        labelKey: 'suggest_alloy',
        value: null,
        displayValue: sf.material_alloy,
        confidence: cf.material_alloy ?? 0.9,
        source: 'field_material',
        alreadyHasValue: !isEmptyValue(quotation?.alloy_code),
        infoOnly: true,
        warning: 'suggest_warn_alloy_not_in_config',
      });
    }
  }

  // Dimensões: top-3 → L, W, H. Confidence baixa em H gera aviso.
  if (dims.length >= 1) {
    const c = dims[0];
    push({
      id: 'piece_length_mm',
      path: 'quotation.piece_length_mm',
      section: 'quotation',
      field: 'piece_length_mm',
      labelKey: 'suggest_piece_length',
      value: Number(c.value),
      displayValue: `${c.value.toFixed(c.value % 1 === 0 ? 0 : 1)} mm`,
      confidence: c.confidence ?? 0.6,
      source: 'dimension_top1',
      alreadyHasValue: !isEmptyValue(quotation?.piece_length_mm),
    });
  }
  if (dims.length >= 2) {
    const c = dims[1];
    push({
      id: 'piece_width_mm',
      path: 'quotation.piece_width_mm',
      section: 'quotation',
      field: 'piece_width_mm',
      labelKey: 'suggest_piece_width',
      value: Number(c.value),
      displayValue: `${c.value.toFixed(c.value % 1 === 0 ? 0 : 1)} mm`,
      confidence: c.confidence ?? 0.6,
      source: 'dimension_top2',
      alreadyHasValue: !isEmptyValue(quotation?.piece_width_mm),
    });
  }
  if (dims.length >= 3) {
    const c = dims[2];
    const conf = c.confidence ?? 0.5;
    push({
      id: 'piece_height_mm',
      path: 'quotation.piece_height_mm',
      section: 'quotation',
      field: 'piece_height_mm',
      labelKey: 'suggest_piece_height',
      value: Number(c.value),
      displayValue: `${c.value.toFixed(c.value % 1 === 0 ? 0 : 1)} mm`,
      confidence: conf,
      source: 'dimension_top3',
      alreadyHasValue: !isEmptyValue(quotation?.piece_height_mm),
      warning: conf < HEIGHT_WARNING_THRESHOLD ? 'suggest_warn_height_low_conf' : undefined,
    });
  }

  return suggestions;
}

/**
 * Campos de cotação a gravar a partir de uma lista de sugestões (auto-apply
 * ou escolhidas no painel). Espelha o ramo quotation de applySuggestions.
 */
export function quotationUpdatesFromSuggestions(list, alloys = []) {
  const quotationUpdates = {};
  for (const s of list || []) {
    if (s.infoOnly || s.section !== 'quotation') continue;
    quotationUpdates[s.field] = s.value;
    if (s.field === 'alloy_code') {
      const alloy = (alloys || []).find((a) => a.code === s.value);
      if (alloy) {
        quotationUpdates.base_mb_eur_per_t = alloy.base_mb_eur_per_t;
        quotationUpdates.alloy_premium_eur_per_t = alloy.premium_eur_per_t;
        quotationUpdates.alloy_premium_sell_eur_per_t = alloy.premium_sell_eur_per_t;
        quotationUpdates.paf_percent = alloy.default_paf_percent;
      }
    }
  }
  return quotationUpdates;
}

/**
 * Devolve um id único para um conjunto de sugestões (usado para evitar
 * re-aplicar auto-fill em renders subsequentes do mesmo extraction).
 */
export function suggestionsKey(extraction) {
  if (!extraction) return null;
  // extracted_at é actualizado em cada extracção/re-extracção
  return extraction.extracted_at || null;
}

export const SUGGESTION_CONSTANTS = {
  AUTO_APPLY_THRESHOLD,
  HEIGHT_WARNING_THRESHOLD,
};
